import { todayString } from "../../utils/dateUtils";

// My Tasks sections. A task lands in the FIRST section (in the user's order)
// whose rule matches it; incomplete tasks never match "completed" and
// completed tasks only match "completed". Tasks matching nothing are grouped
// under OTHER_SECTION so nothing silently disappears.

export const SECTION_TYPES = [
  { type: "overdue", label: "Overdue", hint: "Past their due date" },
  { type: "today", label: "Today", hint: "Due today" },
  { type: "tomorrow", label: "Tomorrow", hint: "Due tomorrow" },
  { type: "this_week", label: "This week", hint: "Due by Sunday" },
  { type: "next_week", label: "Next week", hint: "Due next Monday–Sunday" },
  {
    type: "later",
    label: "Later",
    hint: "Any date from today on not caught above",
  },
  { type: "no_date", label: "No due date", hint: "Tasks without a due date" },
  { type: "completed", label: "Completed", hint: "Finished tasks" },
  { type: "range", label: "Custom range", hint: "Due between two day offsets" },
];

export const DEFAULT_SECTIONS = [
  { id: "overdue", name: "Overdue", type: "overdue" },
  { id: "today", name: "Today", type: "today" },
  { id: "tomorrow", name: "Tomorrow", type: "tomorrow" },
  { id: "later", name: "Later", type: "later" },
  { id: "no_date", name: "No due date", type: "no_date" },
  { id: "completed", name: "Completed", type: "completed" },
];

export const OTHER_SECTION = { id: "__other", name: "Other", type: "other" };

// How far ahead we look for a date that lands in a section when a task is
// dropped on it.
const DROP_SEARCH_DAYS = 800;

function toUtc(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

export function addDaysTo(dateStr, days) {
  const date = new Date(toUtc(dateStr) + days * 86400000);
  return date.toISOString().slice(0, 10);
}

function dayOffset(dueDate, today) {
  return Math.round((toUtc(dueDate) - toUtc(today)) / 86400000);
}

// Days from today to the coming Sunday (weeks run Monday–Sunday).
function daysToWeekEnd(today) {
  const weekday = new Date(toUtc(today)).getUTCDay(); // 0 = Sunday
  return weekday === 0 ? 0 : 7 - weekday;
}

export function sectionMatches(section, task, today) {
  if (section.type === "completed") return task.completed;
  if (task.completed) return false;
  if (section.type === "no_date") return !task.dueDate;
  if (!task.dueDate) return false;

  const offset = dayOffset(task.dueDate, today);
  const weekEnd = daysToWeekEnd(today);
  switch (section.type) {
    case "overdue":
      return offset < 0;
    case "today":
      return offset === 0;
    case "tomorrow":
      return offset === 1;
    case "this_week":
      return offset >= 0 && offset <= weekEnd;
    case "next_week":
      return offset > weekEnd && offset <= weekEnd + 7;
    case "later":
      return offset >= 0;
    case "range":
      return (
        offset >= section.from && (section.to === null || offset <= section.to)
      );
    default:
      return false;
  }
}

export function groupTasksBySection(tasks, sections, today = todayString()) {
  const groups = sections.map((section) => ({ section, tasks: [] }));
  const other = [];
  for (const task of tasks) {
    const group = groups.find((g) => sectionMatches(g.section, task, today));
    if (group) group.tasks.push(task);
    else other.push(task);
  }
  if (other.length > 0) groups.push({ section: OTHER_SECTION, tasks: other });
  return groups;
}

// What dropping (or adding) a task into a section means, or null if the
// section can't be targeted (e.g. Overdue). For date sections this is the
// earliest date from today that would actually land in that section.
export function sectionTarget(section, sections, today = todayString()) {
  if (section.type === "completed") return { completed: true };
  if (section.type === "no_date") return { completed: false, dueDate: null };
  if (section.type === "overdue" || section.type === "other") return null;

  for (let offset = 0; offset <= DROP_SEARCH_DAYS; offset += 1) {
    const probe = { completed: false, dueDate: addDaysTo(today, offset) };
    const first = sections.find((s) => sectionMatches(s, probe, today));
    if (first?.id === section.id)
      return { completed: false, dueDate: probe.dueDate };
  }
  return null;
}

export function describeSectionRule(section) {
  if (section.type === "range") {
    const to = section.to === null ? "onwards" : `to ${section.to}`;
    return `Due from day ${section.from} ${to} (0 = today)`;
  }
  return SECTION_TYPES.find((t) => t.type === section.type)?.hint || "";
}
